import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import {
  BuildingIcon,
  LogOutIcon,
  MoreVerticalIcon,
  UserIcon,
  UsersIcon,
  Sun,
  Moon,
  Monitor,
  CheckIcon,
  Sparkles,
  Zap,
  Globe,
} from "lucide-react"
import { SUPPORTED_LANGUAGES } from "@/lib/languages"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useTheme } from "@/components/theme-provider"
import { UpgradePlanModal } from "@/components/modals/UpgradePlanModal"

interface Organization {
  id: number
  name: string
  credits_remaining: number
  current_plan: string
  admin_emails: string[]
}

export function NavUser({
  user,
  organization: propOrganization,
  hideUpgradeButton = false,
}: {
  user: {
    name: string
    email: string
    avatar_url?: string | null
    admin?: boolean
    super_admin?: boolean
    organization_role?: string
  }
  organization?: Organization
  hideUpgradeButton?: boolean
}) {
  const { isMobile } = useSidebar()
  const { theme, setTheme } = useTheme()
  const { t, i18n } = useTranslation(['common', 'profile'])
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const currentLanguage = i18n.language?.split('-')[0] || 'en'

  // Try to get organization from props first, then from page props
  const { props } = usePage<any>()
  const organization = propOrganization || props.organization

  const handleLogout = () => {
    router.delete('/logout')
  }

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const initials = getUserInitials(user.name)

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground focus-visible:ring-ring"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar_url || undefined} alt={user.name} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
              <MoreVerticalIcon className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 max-w-80 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar_url || undefined} alt={user.name} />
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => router.visit('/profile')}>
                <UserIcon />
                {t('navigation.profile')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => router.visit('/organization')}>
                <BuildingIcon />
                {t('navigation.organization')}
              </DropdownMenuItem>
              {user.super_admin && (
                <DropdownMenuItem onClick={() => router.visit('/admin')}>
                  <UsersIcon />
                  {t('navigation.admin')}
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            {!hideUpgradeButton && organization && organization.credits_remaining !== undefined && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-yellow-500" />
                      <span className="text-muted-foreground">{t('sidebar.credits')}</span>
                    </div>
                    <span className={`font-semibold ${
                      organization.credits_remaining < 10
                        ? 'text-red-600'
                        : organization.credits_remaining < 50
                        ? 'text-orange-600'
                        : 'text-green-600'
                    }`}>
                      {Number(organization.credits_remaining).toFixed(1)}
                    </span>
                  </div>
                </div>
                {user.organization_role === 'admin' && organization.credits_remaining < 100 && (
                  <div
                    onClick={() => setIsUpgradeModalOpen(true)}
                    className="mx-1 mb-1 cursor-pointer rounded-md bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-white transition-none hover:from-purple-500 hover:to-blue-500"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      <span className="text-sm font-medium">{t('sidebar.upgradePlan')}</span>
                    </div>
                  </div>
                )}
                {user.organization_role !== 'admin' && organization.credits_remaining <= 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground break-words">
                    {t('sidebar.contactAdmin', { email: organization.admin_emails?.[0] || '' })}
                  </div>
                )}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Sun className="rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span>{t('preferences.theme', { ns: 'profile' })}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => setTheme('light')}>
                  <Sun />
                  <span>{t('preferences.themeLight', { ns: 'profile' })}</span>
                  {theme === 'light' && <CheckIcon className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('dark')}>
                  <Moon />
                  <span>{t('preferences.themeDark', { ns: 'profile' })}</span>
                  {theme === 'dark' && <CheckIcon className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme('system')}>
                  <Monitor />
                  <span>{t('preferences.themeSystem', { ns: 'profile' })}</span>
                  {theme === 'system' && <CheckIcon className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Globe />
                <span>{t('preferences.language', { ns: 'profile' })}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {SUPPORTED_LANGUAGES.map(({ code, nativeName }) => (
                  <DropdownMenuItem key={code} onClick={() => i18n.changeLanguage(code)}>
                    <span>{nativeName}</span>
                    {currentLanguage === code && <CheckIcon className="ml-auto h-4 w-4" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOutIcon />
              {t('navigation.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <UpgradePlanModal
        open={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
      />
    </SidebarMenu>
  )
}
